import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar";
import DataTable, { TableColumn } from "../components/ui/DataTable";
import AddNewButton from "../components/ui/AddNewButton";
import Toggle from "../components/ui/Toggle";
import Modal from "../components/layout/Modal";
import DeleteModal from "../components/layout/DeleteModal";
import ConfirmModal from "../components/layout/ConfirmModal";
import OfferForm, { OfferFormValues } from "../components/offers/OfferForm";
import EmptyState from "../components/ui/EmptyState";
import TablePagination from "../components/ui/TablePagination";
import { useOffers, useUpdateOffer, useDeleteOffer, useTriggerOfferBroadcast, useOfferBroadcast, useOfferBroadcasts, Offer } from "../services/offers";
import ROUTES from "../utils/ROUTES";
import { toast } from "../hooks/use-toast";
import StatusPill from "../components/ui/StatusPill";
import { Info, Mail, Pencil, Trash2 } from "lucide-react";
import axios from "axios";

const getErrorMessage = (err: unknown): string =>
  axios.isAxiosError(err) ? err.response?.data?.error ?? err.message : "Something went wrong. Please try again.";

const OfferNameCell = ({ offer }: { offer: Offer }) => {
  const { data: broadcasts } = useOfferBroadcasts(offer.id);
  const noEmailsSent = Array.isArray(broadcasts) && broadcasts.length === 0;
  return (
    <div className="flex flex-col gap-[2px]">
      <div className="flex items-center gap-1">
        <span className="text-[16px] font-bold leading-none text-content-heading">{offer.name}</span>
        {noEmailsSent && (
          <div className="relative flex items-center group/tooltip">
            <Info className="w-[14px] h-[14px] text-content-muted cursor-pointer shrink-0" />
            <div className="absolute left-5 top-1/2 -translate-y-1/2 z-20 hidden group-hover/tooltip:block w-max max-w-[220px] rounded-[6px] bg-content-heading px-3 py-2 text-[12px] text-white shadow-lg">
              Broadcast emails have not been sent to customers and must be sent manually.
            </div>
          </div>
        )}
      </div>
      <span className="text-[14px] font-normal text-content-label">{offer.description.length > 250 ? offer.description.slice(0, 250) + "…" : offer.description}</span>
    </div>
  );
};

const Offers = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pollingOfferId, setPollingOfferId] = useState<number | null>(() => {
    const stored = localStorage.getItem("offer_broadcast_polling_id");
    return stored ? Number(stored) : null;
  });
  const [pollingBroadcastId, setPollingBroadcastId] = useState<number | null>(() => {
    const stored = localStorage.getItem("offer_broadcast_polling_broadcast_id");
    return stored ? Number(stored) : null;
  });

  const updatePollingIds = (offerId: number | null, broadcastId: number | null) => {
    setPollingOfferId(offerId);
    setPollingBroadcastId(broadcastId);
    if (offerId === null) {
      localStorage.removeItem("offer_broadcast_polling_id");
      localStorage.removeItem("offer_broadcast_polling_broadcast_id");
    } else {
      localStorage.setItem("offer_broadcast_polling_id", String(offerId));
      localStorage.setItem("offer_broadcast_polling_broadcast_id", String(broadcastId));
    }
  };

  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [activateOnSave, setActivateOnSave] = useState(false);
  const [deletingOffer, setDeletingOffer] = useState<Offer | null>(null);
  const [broadcastingOffer, setBroadcastingOffer] = useState<Offer | null>(null);

  const { data: offersData, isLoading, isError } = useOffers(currentPage);
  const offers = offersData?.offers;
  const pagination = offersData?.pagination;
  const updateOffer = useUpdateOffer();
  const deleteOffer = useDeleteOffer();
  const { mutate: triggerBroadcast } = useTriggerOfferBroadcast();
  const { data: broadcastData } = useOfferBroadcast(
    pollingOfferId ?? 0,
    pollingBroadcastId ?? 0,
    { polling: !!pollingOfferId && !!pollingBroadcastId }
  );
  const isBroadcastActive =
    broadcastData?.status === "pending" || broadcastData?.status === "processing";

  useEffect(() => {
    if (pollingOfferId && broadcastData && !isBroadcastActive) {
      updatePollingIds(null, null);
    }
  }, [broadcastData, isBroadcastActive, pollingOfferId]);

  const handleConfirmDelete = () => {
    if (!deletingOffer) return;
    deleteOffer.mutate(deletingOffer.id, { onSuccess: () => setDeletingOffer(null) });
  };

  const handleConfirmBroadcast = () => {
    if (!broadcastingOffer) return;
    triggerBroadcast(broadcastingOffer.id, {
      onSuccess: (b) => { updatePollingIds(broadcastingOffer.id, b.broadcast_id); setBroadcastingOffer(null); },
      onError: (err) => { toast({ variant: "error", title: "Failed to send broadcast", description: getErrorMessage(err) }); setBroadcastingOffer(null); },
    });
  };

  const handleSave = (values: OfferFormValues) => {
    if (!editingOffer) return;
    updateOffer.mutate(
      {
        id: editingOffer.id,
        body: {
          name: values.name,
          description: values.description,
          tags: values.tags,
          start_date: new Date(values.start_date).toISOString(),
          expiry_date: new Date(values.expiry_date).toISOString(),
          image_url: values.image_url,
          redeemable_only_once: values.redeemable_only_once,
          ...(activateOnSave && { is_enabled: true }),
        },
      },
      {
        onSuccess: () => { toast({ variant: "success", title: "Offer updated", description: "The offer has been successfully updated." }); setEditingOffer(null); setActivateOnSave(false); },
        onError: (err) => { toast({ variant: "error", title: "Failed to update offer", description: getErrorMessage(err) }); },
      }
    );
  };

  const handleToggle = (offer: Offer, is_enabled: boolean) => {
    if (is_enabled && new Date(offer.expiry_date) < new Date()) {
      setEditingOffer(offer);
      setActivateOnSave(true);
      return;
    }
    const { name, description, tags, start_date, expiry_date, image_url } = offer;
    updateOffer.mutate({ id: offer.id, body: { name, description, tags, start_date, expiry_date, image_url, is_enabled } });
  };

  const columns: TableColumn<Offer>[] = [
    {
      key: "image",
      header: "Image",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <div className="w-12 h-12 rounded-[2px] border border-[#c2c7c9] overflow-hidden shrink-0">
          <img src={row.image_url} alt={row.name} className="w-full h-full object-cover" />
        </div>
      ),
    },
    {
      key: "name",
      header: "Offer Name",
      cellClassName: "px-6 py-[22px]",
      render: (row) => <OfferNameCell offer={row} />,
    },
    {
      key: "expiry_date",
      header: "Expiry Date",
      cellClassName: "px-6 py-[30px] whitespace-nowrap",
      render: (row) => (
        <span className="text-[14px] text-content-body">
          {new Date(row.expiry_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      cellClassName: "px-6 py-[30px]",
      render: (row) => <StatusPill status={row.is_enabled ? "Active" : "InActive"} />,
    },
    {
      key: "actions",
      header: "Actions",
      headerAlign: "right",
      width: "180px",
      cellClassName: "px-6 py-4",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <div className="relative group">
            <button
              onClick={() => setEditingOffer(row)}
              className="p-[6px] hover:opacity-70 transition-opacity"
              aria-label="Edit offer"
            >
              <Pencil className="w-[15px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Edit offer</span>
          </div>
          <div className="relative group">
            <button
              onClick={() => setDeletingOffer(row)}
              className="p-[6px] hover:opacity-70 transition-opacity"
              aria-label="Delete offer"
            >
              <Trash2 className="w-[13px] h-[15px]" />
            </button>
            <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Delete offer</span>
          </div>
          {row.is_enabled && (
            <div className="relative group">
              <button
                onClick={() => setBroadcastingOffer(row)}
                disabled={row.id === pollingOfferId && isBroadcastActive}
                className="p-[6px] hover:opacity-70 transition-opacity text-content-label disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Send broadcast email"
              >
                <Mail className="w-[15px] h-[15px]" />
              </button>
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block whitespace-nowrap rounded-[4px] bg-content-heading px-2 py-1 text-[11px] text-white shadow-md z-20">Send broadcast email</span>
            </div>
          )}
          <div className="pl-2 flex items-center">
            <Toggle
              enabled={row.is_enabled}
              onChange={(val) => handleToggle(row, val)}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-surface-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <main className="flex-1 min-w-0 overflow-y-auto px-4 sm:px-8 lg:pl-11 lg:pr-8 pt-6 lg:pt-[60px] pb-16">
        {/* Mobile hamburger */}
        <div className="flex items-center mb-4 lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-black/5 transition-colors"
            aria-label="Open menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="#012926" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>

        {/* Page title row */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              className="text-[28px] sm:text-[34px] uppercase tracking-[0.68px] leading-none text-brand-dark"
              style={{ fontFamily: '"Bebas Neue", sans-serif' }}
            >
              Offers
            </h1>
            <p className="mt-3 text-[14px] sm:text-[16px] font-medium text-content-label">
              Real-time overview of program health and customer engagement.
            </p>
          </div>

          <AddNewButton onClick={() => navigate(ROUTES.ADD_OFFER)} />
        </div>

        <div className="mt-8 lg:mt-[36px]">
          {isLoading && <p className="text-[14px] text-content-label">Loading…</p>}
          {isError && <p className="text-[14px] text-content-error">Failed to load offers.</p>}
          {!isLoading && !isError && (
            !offers?.length
              ? <EmptyState message="No offers found." />
              : (
                  <>
                    <DataTable columns={columns} rows={offers!} rowKey={(o) => String(o.id)} />
                    <TablePagination currentPage={currentPage} pagination={pagination} rowCount={offers!.length} onPageChange={setCurrentPage} />
                  </>
                )
          )}
        </div>
      </main>

      <Modal
        isOpen={editingOffer !== null}
        onClose={() => { setEditingOffer(null); setActivateOnSave(false); }}
        title={`Edit "${editingOffer?.name ?? ""}"`}
      >
        <OfferForm
          key={editingOffer?.id}
          initialValues={{
            name: editingOffer?.name,
            description: editingOffer?.description,
            tags: editingOffer?.tags,
            start_date: editingOffer?.start_date?.split("T")[0],
            expiry_date: editingOffer?.expiry_date?.split("T")[0],
            image_url: editingOffer?.image_url,
            redeemable_only_once: editingOffer?.redeemable_only_once,
          }}
          onSave={handleSave}
        />
      </Modal>

      <DeleteModal
        isOpen={deletingOffer !== null}
        onClose={() => setDeletingOffer(null)}
        onConfirm={handleConfirmDelete}
        title="Are you sure you want to delete this offer?"
        message={
          <>
            This action will permanently remove the{" "}
            <span
              className="font-semibold text-content-body"
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              &ldquo;{deletingOffer?.name}&rdquo;
            </span>{" "}
            offer from your system. This action cannot be undone.
          </>
        }
        confirmLabel="Delete Offer"
        cancelLabel="Cancel"
      />

      <ConfirmModal
        isOpen={broadcastingOffer !== null}
        onClose={() => setBroadcastingOffer(null)}
        onConfirm={handleConfirmBroadcast}
        title="Send broadcast email?"
        message={<>This will send a broadcast email for the <span className="font-semibold text-content-body" style={{ fontFamily: "Inter, sans-serif" }}>&ldquo;{broadcastingOffer?.name}&rdquo;</span> offer to all eligible customers.</>}
        confirmLabel="Send Broadcast"
        cancelLabel="Cancel"
      />
    </div>
  );
};

export default Offers;
